import _ from 'lodash';

import { Modifier } from '../../florence/types/Modifier';
import { getInvalidComboReasons } from '../../florence/utils/getInvalidComboReasons';
import { store } from '../stores/rootStore';
import { CciResult, Code, CodeCombination, SavedCode } from '../types/Codes';
import { EncounterSession } from '../types/EncounterSession';
import { ExportPdfRequest } from '../types/ExportPdfRequest';
import { FileUploadRequest } from '../types/FileUploadRequest';
import { SaveSheet, SheetToSave, UpdateSheetPayload } from '../types/SaveSheet';
import { Search, SearchExtendedSurgeryLayer2 } from '../types/Search';
import {
  SearchResults,
  SearchResultsExtended,
  SearchResultsExtendedLayer2,
} from '../types/SearchResults';
import { User } from '../types/User';

import commonApi from './CommonApi';
import { ENDPOINTS } from './endpoints';

import apiInstance from '.';

const gatewayURL = process.env.REACT_APP_API_GATEWAY_URL as string;

const florenceApi = apiInstance.injectEndpoints({
  overrideExisting: false,
  endpoints: (builder) => ({
    search: builder.query<
      { results: SearchResultsExtended; searchResultId: number },
      Search
    >({
      query: ({
        text,
        sheetId,
        includeTerms,
        excludeTerms,
        searchType,
        limit,
        smartLaunch,
        forceSurgeryEngine,
      }) => ({
        url:
          searchType === 'scan'
            ? `${ENDPOINTS.apiScanSearch}`
            : `${ENDPOINTS.apiKeywordSearch}`,
        method: searchType === 'scan' ? 'POST' : 'GET',
        body:
          searchType === 'scan'
            ? {
                text,
                includeTerms: includeTerms.join(','),
                excludeTerms: excludeTerms.join(','),
                sheetId,
                limit: limit === -1 ? undefined : limit,
                smartLaunch,
                forceSurgeryEngine,
              }
            : undefined,
        params:
          searchType !== 'scan'
            ? {
                text,
                sheetId,
                includeTerms: includeTerms.join(','),
                excludeTerms: excludeTerms.join(','),
                limit: limit === -1 ? undefined : limit,
              }
            : undefined,
      }),
      async onQueryStarted({ searchType }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(commonApi.endpoints.getMe.initiate());
        } catch (err) {
          console.log(err);
        }
      },
      transformResponse: (response: {
        results: SearchResults;
        searchResultId: number;
      }) => {
        response.results.codes = response.results.codes.map((code, index) => {
          if (!code.multiplier) code.multiplier = 1;
          if (!code.modifiers) code.modifiers = [];
          code.order = response.results.codes.length - 1 - index;
          code.searchResultId = response.searchResultId;
          return code;
        });
        return response;
      },
    }),
    searchExtendedSurgeryLayer2: builder.query<
      SearchResultsExtendedLayer2,
      SearchExtendedSurgeryLayer2
    >({
      query: ({ text, foundCodes, sheetId }) => ({
        url: `${ENDPOINTS.apiSearchExtendedSurgeryLayer2}`,
        method: 'POST',
        body: {
          text,
          foundCodes,
          sheetId,
        },
      }),
    }),
    cancelStripeSubscription: builder.mutation<void, void>({
      query: () => ({
        url: `${ENDPOINTS.cancelStripeSubscription}`,
        method: 'POST',
      }),
    }),
    viewedWelcomeNote: builder.mutation<User, void>({
      query: () => ({
        url: `${ENDPOINTS.welcomeNote}`,
        method: 'PATCH',
      }),
    }),
    addFavoriteCodes: builder.mutation<void, Code[]>({
      query: (codes) => ({
        url: `${ENDPOINTS.favoriteCodes}`,
        method: 'POST',
        body: _.chain(codes)
          .map((code) => _.omit(code, ['relevant', 'id', 'selected']))
          .value(),
      }),
      invalidatesTags: ['FavoriteCodes'],
    }),
    deleteFavoriteCode: builder.mutation<void, Pick<SavedCode, 'code'>>({
      query: ({ code }) => ({
        url: `${ENDPOINTS.favoriteCodes}${code}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['FavoriteCodes'],
      async onQueryStarted({ code }, { dispatch, queryFulfilled, getState }) {
        const userID = (getState() as any).model.user?.id;
        if (!userID) return;
        const patchResult = dispatch(
          commonApi.util.updateQueryData(
            'getFavoriteCodes',
            undefined,
            (draft) => {
              const index = draft.findIndex((oldCode) => oldCode.code === code);
              if (index !== -1) draft.splice(index, 1);
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    getNoteByID: builder.query<SaveSheet, string>({
      query: (id) => ({
        url: `${ENDPOINTS.notes}${id}`,
        method: 'GET',
      }),
      keepUnusedDataFor: 0,
    }),
    deleteNote: builder.mutation<void, Pick<SaveSheet, 'sheetId'>>({
      query: ({ sheetId }) => ({
        url: `${ENDPOINTS.notes}${sheetId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['SavedSheets'],
    }),

    fileUploadRequest: builder.query<string, FileUploadRequest>({
      query: (request) => ({
        url: `https://${gatewayURL}upload-file`,
        method: 'POST',
        body: request,
      }),
      transformResponse: (response: { uploadUrl: string }) =>
        response.uploadUrl,
    }),
    exportPDF: builder.mutation<
      { fileLink: string; fileName: string },
      ExportPdfRequest
    >({
      query: (request) => ({
        url: `${ENDPOINTS.apiExportPDF}`,
        method: 'POST',
        body: request,
      }),
    }),
    uploadFile: builder.mutation<
      unknown,
      { url: string; body: ArrayBuffer; contentType: string }
    >({
      query: ({ url, body, contentType }) => ({
        url,
        method: 'PUT',
        body,
        headers: {
          'Content-Type': contentType,
        },
      }),
    }),

    saveMultipleNotes: builder.mutation<SaveSheet[], SheetToSave[]>({
      query: (sheets) => ({
        url: `${ENDPOINTS.notes}`,
        method: 'POST',
        body: sheets,
      }),
    }),
    updateNote: builder.mutation<void, UpdateSheetPayload>({
      query: (saveSheet) => ({
        url: `${ENDPOINTS.notes}`,
        method: 'PATCH',
        body: saveSheet,
      }),
      invalidatesTags: ['SavedSheets'],
    }),
    getModifiers: builder.query<Modifier[], void>({
      query: () => ({
        url: `${ENDPOINTS.getModifiers}`,
        method: 'GET',
      }),
      keepUnusedDataFor: 9999,
      transformResponse: (response: { codeModifiers: Modifier[] }) => {
        const modifiers = _.sortBy(
          response.codeModifiers.map((m) => {
            m.selected = false;
            return m;
          }),
          ['cptModifier']
        );
        response.codeModifiers = modifiers;
        return modifiers;
      },
    }),
    searchCodes: builder.query<
      Code[],
      { text: string; type: 'CPT' | 'HCPCS' | 'ICD10' }
    >({
      query: ({ text, type }) => ({
        url: `${ENDPOINTS.searchCodes}`,
        method: 'POST',
        body: {
          text,
          type,
        },
      }),
    }),
    checkCodeCombinations: builder.mutation<CodeCombination[], string[]>({
      query: (codes) => ({
        url: `${ENDPOINTS.checkCodeCombinations}`,
        method: 'POST',
        body: {
          cci_codes: codes,
        },
      }),
      transformResponse: (response: CciResult) =>
        getInvalidComboReasons(response),
    }),
    flagCode: builder.mutation<
      void,
      { code: Code; sheetId?: number; searchResultId?: number }
    >({
      query: (body) => ({
        url: `${ENDPOINTS.flagCode}`,
        method: 'PATCH',
        body,
      }),
    }),
    trainModel: builder.mutation<SaveSheet, SaveSheet>({
      query: (body) => ({
        url: `${ENDPOINTS.finalizeNote}`,
        method: 'POST',
        body,
      }),
      transformResponse: (response: { sheet: SaveSheet }) => response.sheet,
      invalidatesTags: ['SavedSheets'],
    }),
    getEncounterSession: builder.query<EncounterSession, string>({
      query: (id) => ({
        url: `${ENDPOINTS.smartEncounter}${id}`,
        method: 'GET',
      }),
    }),
    refreshEncounterSession: builder.mutation<EncounterSession, string>({
      query: (id) => ({
        url: `${ENDPOINTS.smartEncounter}${id}`,
        method: 'GET',
      }),
    }),
    deleteUserAccount: builder.mutation<
      { success: boolean; message: string },
      string
    >({
      query: (accessToken) => ({
        url: `${ENDPOINTS.user}`,
        method: 'DELETE',
        body: {
          accessToken,
        },
      }),
    }),
  }),
});

export const {
  useAddFavoriteCodesMutation,
  useDeleteFavoriteCodeMutation,
  useLazySearchQuery,
  useLazySearchExtendedSurgeryLayer2Query,
  useSearchQuery,
  useViewedWelcomeNoteMutation,
  useCancelStripeSubscriptionMutation,
  useLazyFileUploadRequestQuery,
  useUploadFileMutation,
  useSaveMultipleNotesMutation,
  useUpdateNoteMutation,
  useGetModifiersQuery,
  useSearchCodesQuery,
  useCheckCodeCombinationsMutation,
  useExportPDFMutation,
  useGetNoteByIDQuery,
  useDeleteNoteMutation,
  useTrainModelMutation,
  useFlagCodeMutation,
  useGetEncounterSessionQuery,
  useRefreshEncounterSessionMutation,
  useDeleteUserAccountMutation,
  usePrefetch,
} = florenceApi;

export default florenceApi;
